import { getApps } from 'firebase/app'
import { getAuth, onAuthStateChanged, User } from 'firebase/auth'

import { ENV, useApiAuthorization, useEnv } from '@/utils/env'
import { useCallback } from 'react'

declare global {
  interface Window {
    signInWithCustomToken?: (customToken: string) => Promise<string>
  }
}

// Firebase Auth の初期化（永続化されたユーザーの復元）が終わるまで待って、現在のユーザーを返す。
// initializeApp 前に呼ばれた場合は null。
async function waitForCurrentUser(): Promise<User | null> {
  if (getApps().length === 0) return null
  const auth = getAuth()
  if (auth.currentUser) return auth.currentUser
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe()
      resolve(user)
    })
  })
}

// サインイン済みなら Firebase の ID トークンを返す。未サインインなら null。
// getIdToken は期限が近ければ自動で更新する。
export async function getFirebaseIdToken(): Promise<string | null> {
  const user = await waitForCurrentUser()
  if (!user) return null
  return user.getIdToken()
}

// API の Authorization ヘッダー値を返す。
// - サインイン済み: `Bearer <Firebase ID トークン>`（本人の ID がサーバー側で使われる）
// - 未サインイン: development のみ固定の Basic（localStorage の ikasaId で認証を飛ばす開発手順のため）。
//   production では Basic を使わない。認証不要のエンドポイントだけが呼べる。
export function useAuthorizationHeader() {
  const env = useEnv()
  const fallbackAuthorization = useApiAuthorization()

  return useCallback(async (): Promise<Record<string, string>> => {
    const idToken = await getFirebaseIdToken()
    if (idToken) return { Authorization: `Bearer ${idToken}` }
    if (env !== ENV.PRODUCTION && fallbackAuthorization) {
      return { Authorization: fallbackAuthorization }
    }
    return {}
  }, [env, fallbackAuthorization])
}
